/**
 * Money at the UI boundary. State is integer cents everywhere (root CLAUDE.md
 * §6); dollars exist only in what a person types and what they read.
 * Rates are integer basis points in state; people type percentages.
 */
const AUD_WHOLE = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});
/** `$1,234.50`. Null/undefined render as a hyphen placeholder. */
export function formatCents(cents) {
    if (cents === null || cents === undefined || !Number.isFinite(cents))
        return '-';
    return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}
/** `$1,235` for headline figures where cents are noise: rounded to whole dollars. */
export function formatCentsWhole(cents) {
    if (cents === null || cents === undefined || !Number.isFinite(cents))
        return '-';
    return AUD_WHOLE.format(Math.round(cents / 100));
}
/** At most two decimal places: cents for dollars, 0.01% (1 bp) for percentages. */
const TWO_DP = /^(\d+(\.\d{0,2})?|\.\d{1,2})$/;
/**
 * Parse what a person typed as dollars: "$1,250.50", "1250.5", " 1 250 ".
 * Returns null for empty or unparseable input, for negatives, and for more
 * than two decimal places ("1250.555" is refused, never rounded to a cent).
 */
export function parseDollars(text) {
    const cleaned = text.replace(/[$,\s]/g, '');
    if (cleaned === '')
        return null;
    if (!TWO_DP.test(cleaned))
        return null;
    const n = Number(cleaned);
    return Number.isFinite(n) && n >= 0 ? n : null;
}
/** Dollars to integer cents at the boundary: `Math.round(dollars * 100)`. */
export function dollarsToCents(dollars) {
    return Math.round(dollars * 100);
}
/** Typed text straight to cents, or null when it is not a valid amount. */
export function textToCents(text) {
    const d = parseDollars(text);
    return d === null ? null : dollarsToCents(d);
}
/** Cents back to an editable string, without currency symbol or grouping: 125050 -> "1250.50", 100000 -> "1000". */
export function centsToInput(cents) {
    if (cents === null || cents === undefined || !Number.isFinite(cents))
        return '';
    const whole = Math.trunc(cents / 100);
    const rem = Math.abs(cents % 100);
    return rem === 0 ? String(whole) : `${whole}.${String(rem).padStart(2, '0')}`;
}
/**
 * A typed percentage ("2.5", "10%") to integer basis points (250, 1000).
 * Precision finer than 0.01% (one basis point) is refused, never rounded.
 */
export function textToBps(text) {
    const cleaned = text.replace(/[%\s]/g, '');
    if (cleaned === '')
        return null;
    if (!TWO_DP.test(cleaned))
        return null;
    const n = Number(cleaned);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}
/** Basis points to an editable percentage: 250 -> "2.5", 1000 -> "10". */
export function bpsToInput(bps) {
    if (bps === null || bps === undefined || !Number.isFinite(bps))
        return '';
    const whole = Math.trunc(bps / 100);
    const rem = bps % 100;
    if (rem === 0)
        return String(whole);
    return `${whole}.${String(rem).padStart(2, '0')}`.replace(/0$/, '');
}
/** "2.5%" for display. */
export function formatBps(bps) {
    return `${bpsToInput(bps)}%`;
}
